import { keyframes, styled } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import type { MRT_PaginationState as PaginationState } from "material-react-table";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import useReconcileFeeScheduleStaffing from "_shared/fee-schedules/categories/use-reconcile-fee-schedule-staffing";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";
import { useGetFeeScheduleTemplatesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import {
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { FeeScheduleTagChip } from "work/fee-schedule-templates/_views/_shared/embedded-containers";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import ProposalIssues, { ProposalIssue, ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { type TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import TemplateSelector, { type TemplateSelectorRow } from "_shared/_views/templates/template-selector";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import {
  NO_TEMPLATE_CONTRIBUTIONS,
  TemplateContributions,
  withTemplateDeselected,
  withTemplateSelected,
} from "work/proposals/_views/_shared/template-contributions";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FeeScheduleRedlineSelection from "work/proposals/redlining/field-redlines/_views/fee-schedule-redline-selection";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  flexWrap: "nowrap",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
  },
}));
const FeeScheduleContent = styled(Grid)(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
}));
/** The template's own name in the panel's Name cell, beside its tag chips. */
const TemplateRowName = styled(Typography)(() => ({
  flex: "0 1 auto",
  fontSize: "1em",
  fontWeight: 500,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const CategoryRowText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
const CategoryRowDetail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  lineHeight: 1.3,
  minWidth: 0,
  overflow: "hidden",
}));
const RedlineCategorySelection = styled(FeeScheduleRedlineSelection)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  [paneIsCompact(theme)]: {
    marginTop: theme.spacing(1),
  },
  flex: 1,
  height: "100%",
  minHeight: 0,
  marginLeft: theme.spacing(0),
  minWidth: 0,
  overflow: "hidden",
}));
/** What `pulseBorder` grows its ring to at the peak of the animation, outside the border box. */
const ISSUE_PULSE_SPREAD = 3;
/**
 * The tab's issues, and the box the traverser pulses when one of them is the field you are on.
 *
 * `maxWidth` takes this box's own offsets out by hand. A percentage resolves against the parent's
 * content box and knows nothing about either the 16px this box is pushed right by while it is
 * active or the ring the pulse draws outside its border box — so at full width it reached past the
 * parent's right edge, and the parent's `overflow: hidden` cut the ring and the rounded corner off
 * on that side while leaving the other three intact.
 *
 * A step down from the `body1` the issues are written in, at every width. Two of them at 16px wrap
 * to four lines across this column and take the height out of the table underneath.
 */
const IssueContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    [paneIsCompact(theme)]: {
      margin: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
    },
    alignSelf: "flex-start",
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    boxSizing: "border-box",
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(active ? 2 : 0),
    marginTop: active ? theme.spacing(1) : "initial",
    maxWidth: active
      ? `calc(100% - ${theme.spacing(2)} - ${ISSUE_PULSE_SPREAD * 2}px)`
      : "100%",
    padding: active ? theme.spacing(1) : "initial",
    paddingLeft: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    width: "fit-content",
    "& .MuiTypography-root": {
      fontSize: "0.875rem",
    },
  }),
);

type FeeScheduleTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  isDrawerOpen: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  onCreateCategoryClicked: () => void;
  onExistingCategoryClicked: () => void;
  teamMembers: readonly EntityMember[];
  onFeeScheduleTemplatesUpdated: (templateIds: Guid[], newProposalRedline?: ProposalRedline) => void;
};

/**
 * The same proposal redline, with these people proposed for the vendor team.
 *
 * Adding somebody to an active proposal's team is a redline like any other: the counterparty sees
 * it and may reject it. That is what #5237's "add them to the team" means once a proposal is under
 * negotiation, and it is why this goes through the team redline rather than writing a roster.
 *
 * At module scope so the tab below stays compilable by the React Compiler. See app/CLAUDE.md.
 */
function withStaffingSeatedOnTeamRedline(
  redline: ProposalRedline,
  membersToAdd: readonly EntityMember[],
): ProposalRedline {
  if (membersToAdd.length === 0) return redline;
  if (Masked.isMasked(redline.vendorTeam)) return redline;
  const seated = membersToAdd.reduce(
    (teamRedline, member) => teamRedline.addEntry(member),
    redline.vendorTeam,
  );
  return redline.updateTeamMembersRedline(seated);
}

const FeeScheduleTab = (props: Readonly<FeeScheduleTabProps>) => {
  const INTERFACE_NAME = "ActiveProposalFeeScheduleTab";

  const {
    proposalRedline,
    activeReviewField,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    isDrawerOpen,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    teamMembers,
    onFeeScheduleTemplatesUpdated,
  } = props;

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Guid[]>([]);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatePagination, setTemplatePagination] = useState<PaginationState>(INITIAL_TEMPLATE_PAGINATION);
  const [templateContributions, setTemplateContributions] =
    useState<TemplateContributions>(NO_TEMPLATE_CONTRIBUTIONS);

  const issues: ProposalIssue[] = React.useMemo(() => {
    return (
      props.issues?.entries.filter((issue) =>
        [ProposalField.FeeSchedule, ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.EMPTY)].some((field) =>
          field.isEqualTo(issue.field),
        ),
      ) ?? []
    );
  }, [props.issues?.entries]);

  const isMdDownDisplay = useWorkPaneIsCompact();
  const reconcileStaffing = useReconcileFeeScheduleStaffing();
  // An empty roster is not the same as an unreadable one. A masked vendor team cannot be redlined
  // (`withStaffingSeatedOnTeamRedline` returns its input) and offers nobody to hand a rate to.
  const vendorTeamIsMasked = Masked.isMasked(proposalRedline.vendorTeam);
  const session = useSession();
  const userId = session.user!.id;
  const userLegalEntityId = session.user!.legalEntityId;
  const [templateOwnershipScope, setTemplateOwnershipScope] = useState<TemplateOwnershipScope>("all");

  const templateQueryParams = useMemo(() => {
    const paginationParameters = PaginationParameters.create(templatePagination);
    const filterParameters = new FeeScheduleTemplateFilterParameters({
      entityId: userLegalEntityId,
      creatorUserId: templateOwnershipScope === "own" ? userId : undefined,
    });
    const orderParameters = new FeeScheduleTemplateOrderParameters({
      orderBy: FeeScheduleTemplateOrderField.Name,
      orderDir: OrderDirection.Descending,
    });
    return new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
    });
  }, [templateOwnershipScope, templatePagination, userId, userLegalEntityId]);

  const templatesQueryArgs = useMemo(
    () => ({ query: templateQueryParams, interfaceName: INTERFACE_NAME }),
    [templateQueryParams],
  );
  const templatesQuery = useGetFeeScheduleTemplatesQuery(templatesQueryArgs);
  const paginatedTemplatesJSON = templatesQuery.data;
  const templateFeeSchedules = useMemo(() => {
    if (!templatesQuery.isSuccess) return [];
    return FeeScheduleTemplate.fromJSONArray(paginatedTemplatesJSON?.data ?? []);
  }, [paginatedTemplatesJSON?.data, templatesQuery.isSuccess]);

  async function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    if (!template.id) throw new Error("No template selected");

    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot modify fee schedule when it is masked.");
    }

    try {
      let updatedSelectedTemplateIds = [...selectedTemplateIds];
      const isAlreadySelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));

      let newProposalRedline: ProposalRedline | undefined = undefined;

      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        const { contributions, itemKeysToRemove: categoryKeysToRemove } = withTemplateDeselected({
          contributions: templateContributions,
          templateId: template.id,
          alsoSuppliedItemKeys: getCategoryKeysOfLoadedTemplates(updatedSelectedTemplateIds),
        });
        // Read the ids off the redline rather than off the template, so a category the template added
        // and the user has since deleted by hand is not removed a second time.
        const entriesToRemove = proposalRedline.feeSchedule.redlines.flatMap((categoryRedline) =>
          categoryRedline.currentEntry && categoryKeysToRemove.has(getCategoryKey(categoryRedline.currentEntry))
            ? [categoryRedline.currentEntry.id]
            : [],
        );
        if (entriesToRemove.length) {
          let newFeeScheduleRedline: FeeScheduleRedline = proposalRedline.feeSchedule;
          for (const entryId of entriesToRemove) {
            newFeeScheduleRedline = newFeeScheduleRedline.removeEntryByFieldId(entryId);
          }
          newProposalRedline = proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline);
        }
        setTemplateContributions(contributions);
      } else {
        updatedSelectedTemplateIds.push(template.id);

        // The template's rates say who they are for. Anyone it names who is not on the proposal's
        // vendor team is put to the user first; adding them here is a redline of the team, which
        // the counterparty sees and may reject like any other team change (#5237).
        const staffing = await reconcileStaffing({
          categories: template.categories,
          teamMembers,
          teamIsMasked: vendorTeamIsMasked,
        });
        if (!staffing.applied) return;
        let newFeeScheduleRedline: FeeScheduleRedline = proposalRedline.feeSchedule;
        const addedCategoryKeys: string[] = [];
        for (const category of staffing.categories) {
          if (getIsCategoryAlreadySelected(category)) continue;
          addedCategoryKeys.push(getCategoryKey(category));
          newFeeScheduleRedline = newFeeScheduleRedline.addEntry(category);
        }
        setTemplateContributions(
          withTemplateSelected({
            contributions: templateContributions,
            templateId: template.id,
            suppliedItemKeys: template.categories.map(getCategoryKey),
            addedItemKeys: addedCategoryKeys,
          }),
        );
        newProposalRedline = proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline);
        newProposalRedline = withStaffingSeatedOnTeamRedline(
          newProposalRedline,
          staffing.membersToAddToTeam,
        );
      }

      setSelectedTemplateIds(updatedSelectedTemplateIds);
      onFeeScheduleTemplatesUpdated(updatedSelectedTemplateIds, newProposalRedline);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to apply template.", { variant: "error" });
    }
  }

  // No event to stop here any more: the panel's checkbox stops its own press before it reaches the
  // row, so a category is toggled once whether the checkbox or the row around it was pressed.
  async function handleTemplateCategoryToggled(
    templateCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null,
  ) {
    if (Masked.isMasked(proposalRedline.feeSchedule)) throw new Error("Cannot modify fee schedule when it is masked.");

    if (!templateCategory) return;

    if (getIsCategoryAlreadySelected(templateCategory)) {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.removeEntryByFieldId(templateCategory.id),
      );

      onProposalRedlineChange(updatedRedline, null);
    } else {
      // One category off a template names a person just as a whole template does.
      const staffing = await reconcileStaffing({
        categories: [templateCategory],
        teamMembers,
        teamIsMasked: vendorTeamIsMasked,
      });
      if (!staffing.applied) return;
      let updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.addEntry(staffing.categories[0]),
      );
      updatedRedline = withStaffingSeatedOnTeamRedline(updatedRedline, staffing.membersToAddToTeam);
      onProposalRedlineChange(updatedRedline, null);
    }
  }

  // This tab identifies a category by id everywhere else (`getIsCategoryAlreadySelected`,
  // `removeEntryByFieldId`), so provenance is tracked by id too — keying it by name instead would let
  // a same-name/different-id category be added as a duplicate and then removed in pairs.
  function getCategoryKey(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
    return category.id.value;
  }

  // Supplements the recorded contributions for templates that were already checked when the tab
  // mounted, which have no record of their own. `templateFeeSchedules` is one ownership-filtered
  // page, so this can only ever KEEP a category a still-selected template supplies, never take one
  // away: what it cannot see is covered by the contribution records instead.
  function getCategoryKeysOfLoadedTemplates(templateIds: Guid[]): string[] {
    return templateFeeSchedules
      .filter((loadedTemplate) => templateIds.some((id) => id.isEqualTo(loadedTemplate.id)))
      .flatMap((loadedTemplate) => loadedTemplate.categories.map(getCategoryKey));
  }

  function getIsCategoryAlreadySelected(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null,
  ): boolean {
    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot check category when fee schedule is masked.");
    }

    if (!category) return false;
    return proposalRedline.feeSchedule.redlines.some(
      (categoryRedline) => categoryRedline.field.id?.isEqualTo(category.id) && categoryRedline.currentEntry !== null,
    );
  }

  if (Masked.isMasked(proposalRedline.feeSchedule)) {
    return maskedProposalFieldNotice(MaskedProposalField.FeeSchedule);
  }

  // The fee-schedule template query has no server-side name filter, so the panel narrows the page it
  // already holds: the rows handed to the selector are the templates in hand whose name matches what
  // was typed, compared case-insensitively.
  const templateSearchText = templateSearch.trim().toLocaleLowerCase();
  const templateRows: TemplateSelectorRow[] = [];
  for (const template of templateFeeSchedules) {
    // A template the page cannot address — nothing to check, nothing to apply — so it is not drawn.
    if (!template.id?.value) continue;
    if (!matchesFeeScheduleTemplateSearch(template.name, templateSearchText)) continue;

    const templateRowId = template.id.value;
    const isSelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));
    templateRows.push({
      // The categories the row's expander reveals in place, which is where the list's Collapse put
      // them. By id, like every other category check on this tab.
      children: template.categories.map((category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => ({
        content: (
          <CategoryRowText>
            <TemplateRowName>{category.name.value}</TemplateRowName>
            <CategoryRowDetail>
              {category.description?.value}
              <br />
              {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
            </CategoryRowDetail>
          </CategoryRowText>
        ),
        id: `${templateRowId}:${getCategoryKey(category)}`,
        name: category.name.value,
        selected: getIsCategoryAlreadySelected(category),
        onSelectedChanged: () => handleTemplateCategoryToggled(category),
      })),
      content: (
        <>
          <TemplateRowName>{template.name}</TemplateRowName>
          {template.tags.map((tag) => (
            <FeeScheduleTagChip key={tag.value} size="small" label={tag.value} />
          ))}
        </>
      ),
      id: templateRowId,
      // Some of its categories are already on the redline without the template itself having been
      // applied — the rule the list's own checkbox carried, unchanged.
      indeterminate: !isSelected && template.categories.some((category) => getIsCategoryAlreadySelected(category)),
      name: template.name,
      selected: isSelected,
      onSelectedChanged: () => handleTemplateSelectionToggled(template),
    });
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <>
          <ResizableTemplatePanel minimized={isDrawerOpen} selectedCount={selectedTemplateIds.length}>
            {/*
              One panel, drawn once. The selector brings its own compact accordion, so the second
              copy this tab used to render behind a media query — and the stale duplicate of every
              control in it — is gone; `compact` is the pane measurement, not the viewport's.

              No `tabs`: there is one list of fee schedule templates in the domain and no second
              source to offer, so the strip stays off rather than being given a single tab to draw.
              No `sorting` either — the panel holds every row it is showing, so the table sorts them.
            */}
            <TemplateSelector
              collapsedLabel="Fee Schedule Templates"
              compact={isMdDownDisplay}
              ownership={{
                label: "fee schedule templates",
                value: templateOwnershipScope,
                onChange: setTemplateOwnershipScope,
              }}
              pagination={{
                onChange: setTemplatePagination,
                rowCount: paginatedTemplatesJSON?.headers.totalElements ?? 0,
                value: templatePagination,
              }}
              rows={templateRows}
              search={{ onChange: setTemplateSearch, value: templateSearch }}
              selectedCount={selectedTemplateIds.length}
              status={{
                isError: templatesQuery.isError,
                isFetching: templatesQuery.isFetching,
                isLoading: templatesQuery.isLoading,
                onRetry: () => void templatesQuery.refetch(),
              }}
              subject="fee schedule templates"
            />
          </ResizableTemplatePanel>
          {!isMdDownDisplay && (
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          )}
        </>
      )}
      <FeeScheduleContent size="grow" sx={{ mt: 1 }}>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false}>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <RedlineCategorySelection
          activeReviewField={activeReviewField}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
          disableEditing={disableEditing}
          fieldLockTooltip={fieldLockTooltip}
          feeScheduleRedline={proposalRedline.feeSchedule}
          teamMembers={teamMembers}
          onCreateCategoryClicked={onCreateCategoryClicked}
          onExistingCategoryClicked={onExistingCategoryClicked}
          onFeeScheduleRedlineChange={(
            newFeeScheduleRedline: FeeScheduleRedline,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            try {
              onProposalRedlineChange(
                proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline),
                traversalFieldOverride,
              );
            } catch (error) {
              console.error(error);
              enqueueSnackbar("Failed to update fee schedule. Please try again", {
                variant: "error",
              });
            }
          }}
          onCategoryClicked={(categoryId: Guid) => {
            onTraverseToNewField?.(ProposalField.FeeScheduleCategory(categoryId));
          }}
        />
      </FeeScheduleContent>
    </ListContainer>
  )
};

export default FeeScheduleTab;

/** The page size the tab has always asked the fee-schedule template endpoint for. */
const INITIAL_TEMPLATE_PAGINATION = { pageIndex: 0, pageSize: PaginationParameters.DEFAULT_PAGE_SIZE };

/**
 * Whether a template belongs in the panel under what was typed.
 *
 * At module scope and by name only, because the filtering happens in hand: the fee-schedule template
 * endpoint takes no name filter, so the panel narrows the page it was given rather than asking again.
 */
function matchesFeeScheduleTemplateSearch(name: string, searchText: string): boolean {
  return searchText.length === 0 || name.toLocaleLowerCase().includes(searchText);
}
